import { Collections, MetrcFacilitySubCollections } from '@shared/interfaces/lib/firebaseConstants';
import { IMetrcStrain } from '@shared/interfaces/lib/metrcInterfaces';
import { IStrain } from '@shared/interfaces/lib/interfaces';
import {
    MenuItem,
    Dialog,
    DialogTitle,
    DialogActions,
    DialogContent,
    Typography,
    Divider,
    Button,
    useMediaQuery,
    useTheme,
} from '@material-ui/core';
import React, { useContext, useEffect, useState } from 'react';
import { Form, Validators } from '@happybandit/react-validation';
import { useDispatch, useSelector } from 'react-redux';
import { postCreateStrain } from 'api';
import SelectValid from 'components/SelectValid';
import { selectUser, setError } from 'redux/appSlice';
import { FirebaseContext } from 'firebase_init';

interface IProps {
    strain: IStrain;
    licenseId: string;
    onClose: () => void;
}

const LinkStrain: React.FC<IProps> = ( { strain, licenseId, onClose } ) => {
    const dispatch = useDispatch();
    const { firestore } = useContext( FirebaseContext );
    const user = useSelector( selectUser );
    const theme = useTheme();
    const fullScreen = useMediaQuery( theme.breakpoints.down( 'sm' ) );

    const [linked, setLinked] = useState( '' );
    const [strains, setStrains] = useState<IMetrcStrain[]>( [] );
    const [loading, setLoading] = useState( false );

    useEffect( () => {
        if ( licenseId ) {
            firestore.collection( Collections.metrcFacilities ).doc( licenseId )
                .collection( MetrcFacilitySubCollections.metrcStrains )
                .get()
                .then( ( snap ) => {
                    setStrains( snap.docs.map( ( doc ) => doc.data() as IMetrcStrain ) );
                } );
        }
    }, [licenseId] );

    const handleSubmit = ( valid: boolean ) => {
        if ( !valid ) {
            return;
        }

        link( parseInt( linked ) );
    };

    const link = ( strainId: number ) => {
        setLoading( true );
        const metrcStrains = strain.metrcStrains.concat( [{
            metrcLicense: licenseId,
            metrcStrainId: strainId,
        }] );

        const collection = firestore.collection( Collections.strains );
        collection.doc( strain.id ).update( { metrcStrains } ).then( () => {
            onClose();
        } ).catch( ( e: Error ) => {
            setLoading( false );
            dispatch( setError( e.message ) );
        } );
    };

    if ( !user ) {
        return null;
    }

    const createStrain = () => {
        setLoading( true );
        postCreateStrain( user.token, {
            name: strain.name,
            thc: strain.thc,
            cbd: strain.cbd,
            indica: strain.indicaPercent,
            sativa: strain.sativaPercent,
            facilityId: licenseId,
        } )
            .then( () => {
                firestore.collection( Collections.metrcFacilities ).doc( licenseId )
                    .collection( MetrcFacilitySubCollections.metrcStrains )
                    .where( 'Name', '==', strain.name )
                    .onSnapshot( ( snap ) => {
                        if ( snap.size > 0 ) {
                            const data = snap.docs[0].data() as IMetrcStrain
                            link( data.Id );
                        }
                    } );
            } )
            .catch( ( e ) => {
                setLoading( false );
                dispatch( setError( e.message ) );
            } );
    };

    return (
        <>
            <Dialog fullScreen={fullScreen} open={true} onClose={onClose} aria-labelledby="form-dialog-title">
                <DialogTitle id="form-dialog-title">
                    <Typography variant="h5" style={{ display: 'inline' }} component="p">
                        <strong>
                            {strain.name}
                        </strong>
                    </Typography>
                    <Typography variant="body1" component="p" style={{ display: 'inline' }}>
                        <strong>
                            / Link
                        </strong>
                    </Typography>
                    <Typography variant="body2" component="p">
                        Link strains across systems and facilities to keep a single source of truth.
                    </Typography>
                    <hr style={{ 'borderTop': '#eee' }}/>
                </DialogTitle>
                <DialogContent>
                    <Form autoComplete="off" onSubmit={handleSubmit}>
                        <SelectValid
                            id="link-select-strain"
                            value={linked}
                            label="Metrc Strain"
                            fullWidth
                            validators={[Validators.required( linked )]}
                            onChange={( value ) => setLinked( value )}
                        >
                            <MenuItem value=""/>
                            {strains.map( ( strain ) => (
                                <MenuItem
                                    key={strain.Id}
                                    value={strain.Id.toString()}
                                >
                                    {strain.Name}
                                </MenuItem>
                            ) )}
                        </SelectValid>
                        <Button
                            type="submit"
                            color="primary"
                            disabled={loading}
                        >
                            Link to Strain
                        </Button>
                    </Form>
                    <Divider/>
                    <Button disabled={loading} onClick={createStrain} color="primary">
                        Create New
                    </Button>
                </DialogContent>
                <DialogActions>
                    <Button disabled={loading} onClick={onClose} color="primary">
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default LinkStrain;